/**
 * Pure ROI resolver (deep module).
 *
 * `resolveRoi(ref, structures)` maps a model/clinician structure reference
 * (name or roiNumber) to exactly one structure, or to a recoverable
 * discriminated outcome. It is pure and total: no GPU, no Recoil, no model,
 * same inputs ⇒ same result. Mis-resolution is the dominant clinical hazard,
 * so "silently pick the first match" is deliberately unrepresentable — the
 * union forces every caller (the safety core, the tool-runner, the
 * resolved-target card) to handle ambiguity and not-found. A stateful
 * index/query API is a deferred future seam (untested, not built).
 */
import {
    DicomStructureSet,
    ROI_POINT_TYPES,
} from '~/domain/dicom/dicomstructureset';
import type { AmbiguityReason, ListedStructure } from '../../contract';

/**
 * Whether a DICOM interpreted-type string denotes a single-point ROI
 * (MARKER/ISOCENTER/REGISTRATION). Single source of truth, shared by the
 * resolver and the op-registry so the point-vs-volume rule cannot diverge.
 */
const POINT_TYPES: ReadonlySet<string> = new Set<string>(ROI_POINT_TYPES);
export function isPointRoiType(type: string): boolean {
    return POINT_TYPES.has(type);
}
function isPoint(st: ListedStructure): boolean {
    return isPointRoiType(st.type);
}

/** Deterministic candidate/suggestion order: ascending roiNumber. */
function byRoiNumber(a: ListedStructure, b: ListedStructure): number {
    return a.roiNumber - b.roiNumber;
}

export type Resolution =
    | { readonly kind: 'resolved'; readonly structure: ListedStructure }
    | {
          readonly kind: 'ambiguous';
          readonly reason: AmbiguityReason;
          readonly candidates: ReadonlyArray<ListedStructure>;
      }
    | {
          readonly kind: 'not-found';
          readonly suggestions: ReadonlyArray<ListedStructure>;
      };

/** Clinician/model name match — the SAME trimmed, case-insensitive rule the
 *  rest of the app uses for ROI-name equality (single source of truth). */
function namesEqual(a: string, b: string): boolean {
    return DicomStructureSet.structureNamesEqual(a, b);
}

export function resolveRoi(
    ref: string | number,
    structures: ReadonlyArray<ListedStructure>
): Resolution {
    // A known roiNumber is an exact engine identity — no fuzzy matching, no
    // suggestions (the model only supplies a number it already learned from
    // a prior list_structures result).
    if (typeof ref === 'number') {
        const exact = structures.find((st) => st.roiNumber === ref);
        return exact
            ? { kind: 'resolved', structure: exact }
            : { kind: 'not-found', suggestions: [] };
    }

    const matches = structures
        .filter((st) => namesEqual(st.name, ref))
        .slice()
        .sort(byRoiNumber);

    if (matches.length === 1) {
        return { kind: 'resolved', structure: matches[0] };
    }

    if (matches.length > 1) {
        // A point ROI (marker/isocenter/registration) colliding by name with
        // a volumetric ROI is a distinct, higher-signal hazard than two
        // volumetric duplicates — the caller re-asks differently.
        const hasPoint = matches.some(isPoint);
        const hasVolume = matches.some((st) => !isPoint(st));
        return {
            kind: 'ambiguous',
            reason:
                hasPoint && hasVolume ? 'point-vs-volume' : 'duplicate-name',
            candidates: matches,
        };
    }

    // Recoverable: offer case-insensitive substring neighbours so the caller
    // can re-ask with a concrete pick (deterministic, ascending roiNumber).
    const needle = ref.trim().toLowerCase();
    const suggestions =
        needle.length === 0
            ? []
            : structures
                  .filter((st) => st.name.toLowerCase().includes(needle))
                  .slice()
                  .sort(byRoiNumber);
    return { kind: 'not-found', suggestions };
}
